import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { format } from 'date-fns';
import { Plus, X, Send, Eye, Copy, Trash2, ArrowUp, ArrowDown, Download, MessageCircle, Pause, Play } from 'lucide-react';

// ── Difusión: contactos y campañas ────────────────────────────
// La base de contactos se arma sola con todos los que cotizaron o son
// clientes (difusionModule.js). Las campañas salen por mail cada N días, en
// el orden de la cola; por WhatsApp se mandan de a uno desde acá.

async function llamar(method, path, body) {
  const token = localStorage.getItem('nebb_token');
  const r = await fetch(`/api/difusion${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const t = await r.text();
  let j; try { j = JSON.parse(t); } catch { j = { error: t }; }
  if (!r.ok) throw new Error(j.error || `Error ${r.status}`);
  return j;
}

const fecha = (iso) => { try { return iso ? format(new Date(iso), 'dd/MM/yyyy') : '—'; } catch { return '—'; } };
const fechaDia = (d) => d ? d.split('-').reverse().join('/') : '—';
const ESTADOS = {
  enviando: { label: 'Mandándose', clase: 'badge-amber' },
  pausada: { label: 'Frenada', clase: 'badge-red' },
  en_cola: { label: 'En la cola', clase: 'badge-blue' },
  borrador: { label: 'Borrador', clase: 'badge-gray' },
  enviada: { label: 'Enviada', clase: 'badge-green' },
};
const PUBLICOS = [
  { id: 'todos', label: 'Todos' },
  { id: 'no_emitieron', label: 'Los que cotizaron y no emitieron' },
  { id: 'clientes', label: 'Solo clientes' },
];
const numeroWA = (c) => c;
const linkWA = (cel, texto) => {
  const movil = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  return (movil ? `https://wa.me/${numeroWA(cel)}?text=` : `https://web.whatsapp.com/send?phone=${numeroWA(cel)}&text=`) + encodeURIComponent(texto);
};
const descSegmento = (seg, ramos) => {
  const p = (PUBLICOS.find(x => x.id === (seg && seg.publico)) || PUBLICOS[0]).label;
  const r = seg && seg.ramos && seg.ramos.length ? ` · cotizaron ${seg.ramos.map(x => ramos[x] || x).join(' o ')}` : '';
  const s = seg && seg.sinRamo ? ` · sin los que ya tienen ${(ramos[seg.sinRamo] || seg.sinRamo).toLowerCase()}` : '';
  return p + r + s;
};

export default function DifusionPage() {
  const [tab, setTab] = useState('campanias');
  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Difusión</h1>
          <p className="page-subtitle">Mails y WhatsApp con información de coberturas para todos los que cotizaron o son clientes</p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4, background: 'var(--bg-input)', borderRadius: 10, padding: 4, marginBottom: 18, width: 'fit-content' }}>
        {[['campanias', 'Campañas'], ['contactos', 'Contactos']].map(([id, label]) => (
          <button key={id} type="button" onClick={() => setTab(id)}
            style={{ fontFamily: 'inherit', fontSize: 13.5, fontWeight: tab === id ? 700 : 500, cursor: 'pointer', border: 'none', borderRadius: 8, padding: '8px 18px',
              background: tab === id ? 'var(--accent)' : 'transparent', color: tab === id ? '#fff' : 'var(--text-secondary)' }}>{label}</button>
        ))}
      </div>
      {tab === 'campanias' ? <Campanias /> : <Contactos />}
    </div>
  );
}

// ── Campañas ──────────────────────────────────────────────────
function Campanias() {
  const { showToast } = useApp();
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [editar, setEditar] = useState(null);       // campaña o {} para nueva
  const [wa, setWa] = useState(null);               // campaña para WhatsApp
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(async () => {
    try { setDatos(await llamar('GET', '/campanias')); setError(''); } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  // Mientras se manda una, refresca solo para ver el avance.
  useEffect(() => {
    if (!datos || !datos.campanias.some(c => c.estado === 'enviando')) return undefined;
    const t = setInterval(cargar, 8000);
    return () => clearInterval(t);
  }, [datos, cargar]);

  const accion = async (fn, ok) => {
    setOcupado(true);
    try { await fn(); if (ok) showToast(ok); await cargar(); } catch (e) { alert(e.message); }
    setOcupado(false);
  };

  if (error) return <div className="card" style={{ color: 'var(--red)' }}>{error}</div>;
  if (!datos) return <div className="card" style={{ color: 'var(--text-muted)' }}>Cargando…</div>;
  const { campanias, config, ramos } = datos;
  const grupos = [
    ['enviando', 'Mandándose ahora'], ['pausada', 'Frenadas'], ['en_cola', 'En la cola · salen en este orden'], ['borrador', 'Borradores'], ['enviada', 'Enviadas'],
  ];
  const cola = campanias.filter(c => c.estado === 'en_cola');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Programacion config={config} cola={cola} onGuardar={(c) => accion(() => llamar('PUT', '/config', c), 'Guardado')} />

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn-primary" onClick={() => setEditar({})}><Plus size={15} /> Nueva campaña</button>
      </div>

      {grupos.map(([estado, titulo]) => {
        const lista = campanias.filter(c => c.estado === estado);
        if (!lista.length) return null;
        return (
          <div key={estado}>
            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-muted)', margin: '0 0 8px', letterSpacing: '.03em' }}>{titulo.toUpperCase()}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {lista.map((c, i) => (
                <TarjetaCampania key={c.id} c={c} ramos={ramos} ocupado={ocupado}
                  posicion={estado === 'en_cola' ? i : null} totalCola={cola.length}
                  proximo={estado === 'en_cola' && i === 0 && config.automatico ? config.proximo : ''}
                  onEditar={() => setEditar(c)}
                  onWa={() => setWa(c)}
                  onCola={(acc) => accion(() => llamar('POST', `/campanias/${c.id}/cola`, { accion: acc }))}
                  onDuplicar={() => accion(() => llamar('POST', `/campanias/${c.id}/duplicar`), 'Duplicada como borrador')}
                  onEliminar={() => { if (window.confirm(`¿Eliminar "${c.titulo || c.asunto}"?`)) accion(() => llamar('DELETE', `/campanias/${c.id}`), 'Eliminada'); }}
                  onEnviar={() => {
                    const msg = c.estado === 'pausada'
                      ? `¿Seguir mandando "${c.titulo || c.asunto}" a los que faltan?`
                      : `¿Mandar ahora "${c.titulo || c.asunto}" por mail a ${c.alcanceMail} contactos?\n\nSale en el momento, sin esperar a la cola.`;
                    if (window.confirm(msg)) accion(() => llamar('POST', `/campanias/${c.id}/enviar`, { confirmar: true }), 'Se está mandando');
                  }}
                  onFrenar={() => accion(() => llamar('POST', `/campanias/${c.id}/frenar`), 'Frenada')}
                />
              ))}
            </div>
          </div>
        );
      })}
      {!campanias.length && <div className="card" style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>Todavía no hay campañas.</div>}

      {editar && <Editor campania={editar} ramos={ramos} onCerrar={() => setEditar(null)} onGuardado={() => { setEditar(null); cargar(); }} />}
      {wa && <EnvioWhatsApp campania={wa} onCerrar={() => { setWa(null); cargar(); }} />}
    </div>
  );
}

function Programacion({ config, cola, onGuardar }) {
  const [cada, setCada] = useState(config.cadaDias || 15);
  const [proximo, setProximo] = useState(config.proximo || config.hoy);
  const [tope, setTope] = useState(config.topeDiario || 95);
  useEffect(() => { setCada(config.cadaDias || 15); setProximo(config.proximo || config.hoy); setTope(config.topeDiario || 95); }, [config]);
  const cambiado = Number(cada) !== Number(config.cadaDias) || proximo !== (config.proximo || config.hoy) || Number(tope) !== Number(config.topeDiario);
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Envío automático por mail</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 3 }}>
            {config.automatico
              ? (cola.length ? <>Próximo envío: <b style={{ color: 'var(--text-primary)' }}>{fechaDia(config.proximo)}</b> a las 10 h · sale <b style={{ color: 'var(--text-primary)' }}>{cola[0].titulo || cola[0].asunto}</b>. Te llega un mail el día anterior para revisarla.</> : 'Está prendido pero la cola está vacía: agregá una campaña.')
              : 'Apagado. Revisá las campañas de la cola y prendelo cuando estés listo.'}
          </div>
        </div>
        <button type="button" className={config.automatico ? 'btn btn-secondary' : 'btn btn-primary'}
          onClick={() => onGuardar({ automatico: !config.automatico, proximo: config.automatico ? undefined : proximo })}>
          {config.automatico ? <><Pause size={14} /> Apagar</> : <><Play size={14} /> Prender</>}
        </button>
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="form-group" style={{ width: 130 }}>
          <label className="form-label">Cada cuántos días</label>
          <input type="number" min={1} max={90} value={cada} onChange={e => setCada(e.target.value)} />
        </div>
        <div className="form-group" style={{ width: 170 }}>
          <label className="form-label">Próximo envío</label>
          <input type="date" value={proximo || ''} min={config.hoy} onChange={e => setProximo(e.target.value)} />
        </div>
        <div className="form-group" style={{ width: 170 }}>
          <label className="form-label" title="Lo que no entra en el día sale al día siguiente">Máximo de mails por día</label>
          <input type="number" min={1} value={tope} onChange={e => setTope(e.target.value)} />
        </div>
        {cambiado && <button type="button" className="btn btn-primary btn-sm" onClick={() => onGuardar({ cadaDias: Number(cada), proximo, topeDiario: Number(tope) })}>Guardar</button>}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        El máximo por día depende del plan de Resend (el gratuito permite 100 mails por día y 3.000 por mes). Si la lista es más larga, el resto sale al día siguiente.
      </div>
    </div>
  );
}

function TarjetaCampania({ c, ramos, ocupado, posicion, totalCola, proximo, onEditar, onWa, onCola, onDuplicar, onEliminar, onEnviar, onFrenar }) {
  const es = ESTADOS[c.estado] || ESTADOS.borrador;
  const editable = ['borrador', 'en_cola'].includes(c.estado);
  const e = c.envio;
  return (
    <div className="card" style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap', padding: '16px 18px' }}>
      {posicion != null && <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--text-muted)', width: 24, textAlign: 'center' }}>{posicion + 1}</div>}
      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className={`badge ${es.clase}`}>{es.label}</span>
          {proximo && <span className="badge badge-blue">Sale el {fechaDia(proximo)}</span>}
          <span style={{ fontWeight: 700, fontSize: 15 }}>{c.titulo || c.asunto}</span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 5 }}>Asunto: {c.asunto}</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>
          {descSegmento(c.segmento, ramos)} · <b>{c.alcanceMail}</b> por mail · <b>{c.alcanceWa}</b> por WhatsApp
          {c.waEnviados ? ` · ${c.waEnviados} WhatsApp mandados` : ''}
        </div>
        {e && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
              Mail: {e.enviados} de {e.total} enviados{e.errores ? ` · ${e.errores} con error o de baja` : ''}{c.enviadaEn ? ` · terminó el ${fecha(c.enviadaEn)}` : ` · empezó el ${fecha(e.inicio)}`}
            </div>
            <div style={{ height: 6, background: 'var(--bg-input)', borderRadius: 99, marginTop: 6, overflow: 'hidden', maxWidth: 360 }}>
              <div style={{ height: '100%', width: `${e.total ? Math.round((e.enviados + e.errores) / e.total * 100) : 100}%`, background: 'var(--green)' }} />
            </div>
            {e.detalleErrores && e.detalleErrores.length > 0 && (
              <details style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                <summary style={{ cursor: 'pointer' }}>Ver los que no salieron</summary>
                {e.detalleErrores.map(x => <div key={x.email}>{x.email}: {x.error}</div>)}
              </details>
            )}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {posicion != null && <>
          <button className="btn btn-secondary btn-sm" disabled={ocupado || posicion === 0} title="Subir" onClick={() => onCola('arriba')}><ArrowUp size={13} /></button>
          <button className="btn btn-secondary btn-sm" disabled={ocupado || posicion === totalCola - 1} title="Bajar" onClick={() => onCola('abajo')}><ArrowDown size={13} /></button>
        </>}
        <button className="btn btn-secondary btn-sm" onClick={onEditar}><Eye size={13} /> {editable ? 'Ver y editar' : 'Ver'}</button>
        {c.estado === 'borrador' && <button className="btn btn-secondary btn-sm" disabled={ocupado} onClick={() => onCola('sumar')}>Sumar a la cola</button>}
        {c.estado === 'en_cola' && <button className="btn btn-secondary btn-sm" disabled={ocupado} onClick={() => onCola('sacar')}>Sacar de la cola</button>}
        {(editable || c.estado === 'pausada') && <button className="btn btn-primary btn-sm" disabled={ocupado} onClick={onEnviar}><Send size={13} /> {c.estado === 'pausada' ? 'Seguir mandando' : 'Mandar ahora'}</button>}
        {c.estado === 'enviando' && <button className="btn btn-danger btn-sm" disabled={ocupado} onClick={onFrenar}><Pause size={13} /> Frenar</button>}
        <button className="btn btn-sm" style={{ background: '#25d366', color: '#fff', border: 'none' }} onClick={onWa}><MessageCircle size={13} /> WhatsApp</button>
        <button className="btn btn-secondary btn-sm" disabled={ocupado} title="Duplicar" onClick={onDuplicar}><Copy size={13} /></button>
        {c.estado !== 'enviando' && <button className="btn btn-danger btn-sm" disabled={ocupado} title="Eliminar" onClick={onEliminar}><Trash2 size={13} /></button>}
      </div>
    </div>
  );
}

// ── Editor con vista previa ───────────────────────────────────
function Editor({ campania, ramos, onCerrar, onGuardado }) {
  const { showToast, currentUser } = useApp();
  const nueva = !campania.id;
  const soloVer = !nueva && !['borrador', 'en_cola'].includes(campania.estado);
  const [f, setF] = useState(() => ({
    titulo: campania.titulo || '', asunto: campania.asunto || '', preheader: campania.preheader || '',
    cuerpo: campania.cuerpo || 'Hola {nombre},\n\n', whatsapp: campania.whatsapp || '',
    boton: { texto: (campania.boton && campania.boton.texto) || '', url: (campania.boton && campania.boton.url) || '' },
    segmento: { publico: 'todos', ramos: [], sinRamo: '', ...(campania.segmento || {}) },
  }));
  const [html, setHtml] = useState('');
  const [asuntoVisto, setAsuntoVisto] = useState('');
  const [mailPrueba, setMailPrueba] = useState((currentUser && currentUser.email) || '');
  const [guardando, setGuardando] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const setSeg = (k, v) => setF(x => ({ ...x, segmento: { ...x.segmento, [k]: v } }));
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { const r = await llamar('POST', '/vista-previa', f); setHtml(r.html); setAsuntoVisto(r.asunto); } catch { /* sin vista previa */ }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [f]);

  const guardar = async () => {
    if (!f.asunto.trim()) { alert('Falta el asunto.'); return; }
    setGuardando(true);
    try {
      if (nueva) await llamar('POST', '/campanias', f); else await llamar('PUT', `/campanias/${campania.id}`, f);
      showToast(nueva ? 'Campaña creada como borrador' : 'Guardada');
      onGuardado();
    } catch (e) { alert(e.message); }
    setGuardando(false);
  };
  const probar = async () => {
    try {
      if (nueva) { alert('Guardala primero y después mandate la prueba.'); return; }
      const r = await llamar('POST', `/campanias/${campania.id}/prueba`, { email: mailPrueba, borrador: f });
      showToast(r.simulado ? 'Resend no está configurado: no salió (modo prueba)' : `Prueba mandada a ${mailPrueba}`);
    } catch (e) { alert(e.message); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 1180, width: '96vw' }}>
        <div className="modal-header">
          <h2 style={{ fontSize: 17, fontWeight: 700 }}>{nueva ? 'Nueva campaña' : (soloVer ? 'Campaña enviada' : 'Editar campaña')}</h2>
          <button className="btn btn-secondary btn-sm" onClick={onCerrar}><X size={14} /></button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 20, alignItems: 'start' }}>
          <fieldset disabled={soloVer} style={{ border: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
            <div className="form-group"><label className="form-label">Título (va grande arriba del mail)</label>
              <input value={f.titulo} onChange={e => set('titulo', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Asunto · podés usar {'{nombre}'}</label>
              <input value={f.asunto} onChange={e => set('asunto', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Texto de vista previa (lo que se ve en la bandeja al lado del asunto)</label>
              <input value={f.preheader} onChange={e => set('preheader', e.target.value)} /></div>
            <div className="form-group">
              <label className="form-label">Texto del mail</label>
              <textarea rows={12} value={f.cuerpo} onChange={e => set('cuerpo', e.target.value)} style={{ resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Párrafos separados por una línea en blanco · **negrita** · listas con "- " al principio · {'{nombre}'} pone el primer nombre de cada uno.</div>
            </div>
            <div className="form-grid">
              <div className="form-group"><label className="form-label">Botón: texto</label><input value={f.boton.texto} onChange={e => set('boton', { ...f.boton, texto: e.target.value })} placeholder="Cotizar mi hogar" /></div>
              <div className="form-group"><label className="form-label">Botón: link</label><input value={f.boton.url} onChange={e => set('boton', { ...f.boton, url: e.target.value })} placeholder="https://nebbrokers.com/…" /></div>
            </div>
            <div className="form-group">
              <label className="form-label">Mensaje de WhatsApp (más corto) · si lo dejás vacío se usa el texto del mail</label>
              <textarea rows={5} value={f.whatsapp} onChange={e => set('whatsapp', e.target.value)} style={{ resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>¿A quién le llega?</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {PUBLICOS.map(p => (
                  <button key={p.id} type="button" onClick={() => setSeg('publico', p.id)}
                    style={{ fontFamily: 'inherit', fontSize: 12.5, cursor: 'pointer', borderRadius: 99, padding: '6px 12px', border: `1px solid ${f.segmento.publico === p.id ? 'var(--accent)' : 'var(--border)'}`, background: f.segmento.publico === p.id ? 'var(--accent-dim)' : 'transparent', color: f.segmento.publico === p.id ? 'var(--accent)' : 'var(--text-secondary)', fontWeight: f.segmento.publico === p.id ? 700 : 500 }}>{p.label}</button>
                ))}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Solo los que cotizaron o tienen (si no marcás nada, van todos):</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {Object.entries(ramos).filter(([k]) => k !== 'otro').map(([k, label]) => (
                  <label key={k} style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 12.5, cursor: 'pointer' }}>
                    <input type="checkbox" style={{ width: 'auto' }} checked={f.segmento.ramos.includes(k)} onChange={e => setSeg('ramos', e.target.checked ? [...f.segmento.ramos, k] : f.segmento.ramos.filter(x => x !== k))} /> {label}
                  </label>
                ))}
              </div>
              <div className="form-group" style={{ maxWidth: 320 }}>
                <label className="form-label">Sacar a los que ya tienen</label>
                <select value={f.segmento.sinRamo} onChange={e => setSeg('sinRamo', e.target.value)}>
                  <option value="">— nadie —</option>
                  {Object.entries(ramos).filter(([k]) => k !== 'otro').map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              </div>
            </div>
          </fieldset>
          <div style={{ position: 'sticky', top: 0, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Así le llega a un contacto que se llama Martín:</div>
            <div style={{ fontSize: 13, background: 'var(--bg-input)', borderRadius: 8, padding: '8px 12px' }}><b>{asuntoVisto || f.asunto}</b> <span style={{ color: 'var(--text-muted)' }}>— {f.preheader}</span></div>
            <iframe title="Vista previa" srcDoc={html} style={{ width: '100%', height: '62vh', border: '1px solid var(--border)', borderRadius: 10, background: '#f2f5f8' }} />
            {!nueva && (
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={mailPrueba} onChange={e => setMailPrueba(e.target.value)} placeholder="tu@mail.com" style={{ flex: 1 }} />
                <button type="button" className="btn btn-secondary" onClick={probar}><Send size={14} /> Mandarme una prueba</button>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCerrar}>{soloVer ? 'Cerrar' : 'Cancelar'}</button>
          {!soloVer && <button className="btn btn-primary" disabled={guardando} onClick={guardar}>{guardando ? 'Guardando…' : (nueva ? 'Crear borrador' : 'Guardar')}</button>}
        </div>
      </div>
    </div>
  );
}

// ── WhatsApp de a uno ─────────────────────────────────────────
function EnvioWhatsApp({ campania, onCerrar }) {
  const [lista, setLista] = useState(null);
  const [verTodos, setVerTodos] = useState(false);
  const cargar = useCallback(async () => {
    try { setLista((await llamar('GET', `/campanias/${campania.id}/whatsapp`)).lista); } catch (e) { alert(e.message); }
  }, [campania.id]);
  useEffect(() => { cargar(); }, [cargar]);
  const marcar = async (x, enviado) => {
    setLista(l => l.map(y => y.clave === x.clave ? { ...y, enviado: enviado ? new Date().toISOString() : '' } : y));
    try { await llamar('POST', `/campanias/${campania.id}/whatsapp`, { clave: x.clave, enviado }); } catch (e) { alert(e.message); }
  };
  const mandados = lista ? lista.filter(x => x.enviado).length : 0;
  const visibles = lista ? (verTodos ? lista : lista.filter(x => !x.enviado)) : [];
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 760 }}>
        <div className="modal-header">
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700 }}>WhatsApp · {campania.titulo || campania.asunto}</h2>
            {lista && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>{mandados} de {lista.length} mandados</div>}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={onCerrar}><X size={14} /></button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', background: 'var(--bg-input)', borderRadius: 8, padding: '10px 12px' }}>
            Tocá <b>Abrir WhatsApp</b>: se abre el chat con el mensaje ya escrito, lo mandás y queda marcado. Mandalos de a poco (unos 20 o 30 por día): si WhatsApp ve muchos mensajes iguales seguidos a gente que no te tiene agendado, puede bloquear el número.
          </div>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, cursor: 'pointer', width: 'fit-content' }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={verTodos} onChange={e => setVerTodos(e.target.checked)} /> Mostrar también los ya mandados
          </label>
          {!lista ? <div style={{ color: 'var(--text-muted)' }}>Cargando…</div> : !visibles.length ? (
            <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 24 }}>{lista.length ? '¡Listo! Ya se les mandó a todos.' : 'No hay contactos con celular para esta campaña.'}</div>
          ) : visibles.map(x => (
            <div key={x.clave} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 10, opacity: x.enviado ? 0.6 : 1 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{x.nombre || 'Sin nombre'}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{x.celularLegible}{x.enviado ? ` · mandado el ${fecha(x.enviado)}` : ''}</div>
              </div>
              {x.enviado
                ? <button className="btn btn-secondary btn-sm" onClick={() => marcar(x, false)}>Desmarcar</button>
                : <a className="btn btn-sm" href={linkWA(x.celular, x.texto)} target="_blank" rel="noopener noreferrer" onClick={() => marcar(x, true)}
                    style={{ background: '#25d366', color: '#fff', border: 'none', textDecoration: 'none' }}><MessageCircle size={13} /> Abrir WhatsApp</a>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Contactos ─────────────────────────────────────────────────
function Contactos() {
  const { showToast } = useApp();
  const [datos, setDatos] = useState(null);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState('todos');
  const [ramo, setRamo] = useState('');
  const [limite, setLimite] = useState(100);
  const cargar = useCallback(async () => { try { setDatos(await llamar('GET', '/contactos')); } catch (e) { alert(e.message); } }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const baja = async (k, canal, valor) => {
    try {
      await llamar('POST', '/bajas', { email: k.email, celular: k.celular, canal, baja: valor });
      showToast(valor === false ? 'Vuelve a recibir' : 'Dado de baja');
      cargar();
    } catch (e) { alert(e.message); }
  };
  const exportar = async () => {
    const token = localStorage.getItem('nebb_token');
    const r = await fetch('/api/difusion/contactos.csv', { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { alert('No se pudo exportar'); return; }
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement('a'); a.href = url; a.download = `contactos-nebb-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  if (!datos) return <div className="card" style={{ color: 'var(--text-muted)' }}>Cargando…</div>;
  const { contactos, totales, ramos } = datos;
  const sinT = (x) => String(x || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const q = sinT(busca.trim());
  const lista = contactos.filter(k => {
    if (q && !sinT([k.nombre, k.email, k.celular, k.celularLegible].join(' ')).includes(q)) return false;
    if (ramo && !k.ramos.includes(ramo) && !k.polizas.includes(ramo)) return false;
    if (filtro === 'clientes') return k.cliente || k.emitio;
    if (filtro === 'no_emitieron') return !(k.cliente || k.emitio);
    if (filtro === 'bajas') return k.bajaMail || k.bajaWa;
    return true;
  });
  const caja = { background: 'var(--bg-input)', borderRadius: 10, padding: '12px 14px', flex: '1 1 130px' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={caja}><div style={{ fontSize: 22, fontWeight: 800 }}>{totales.total}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>contactos</div></div>
        <div style={caja}><div style={{ fontSize: 22, fontWeight: 800 }}>{totales.conMail}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>reciben mail</div></div>
        <div style={caja}><div style={{ fontSize: 22, fontWeight: 800 }}>{totales.conCelular}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>con celular para WhatsApp</div></div>
        <div style={caja}><div style={{ fontSize: 22, fontWeight: 800, color: 'var(--green)' }}>{totales.clientes}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>son clientes</div></div>
        <div style={caja}><div style={{ fontSize: 22, fontWeight: 800 }}>{totales.bajas}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>se dieron de baja</div></div>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por nombre, mail o celular…" style={{ flex: '1 1 240px', maxWidth: 340 }} />
        <select value={filtro} onChange={e => setFiltro(e.target.value)} style={{ maxWidth: 220 }}>
          <option value="todos">Todos</option>
          <option value="no_emitieron">Cotizaron y no emitieron</option>
          <option value="clientes">Clientes</option>
          <option value="bajas">Dados de baja</option>
        </select>
        <select value={ramo} onChange={e => setRamo(e.target.value)} style={{ maxWidth: 200 }}>
          <option value="">Cualquier seguro</option>
          {Object.entries(ramos).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{lista.length} contactos</span>
        <button className="btn btn-secondary btn-sm" onClick={exportar}><Download size={13} /> Exportar a Excel (CSV)</button>
      </div>
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Nombre</th><th>Mail</th><th>Celular</th><th>Cotizó</th><th>Tiene</th><th>Última vez</th><th>Recibe</th></tr></thead>
            <tbody>
              {lista.slice(0, limite).map(k => (
                <tr key={k.id}>
                  <td style={{ fontWeight: 600 }}>{k.nombre || '—'}</td>
                  <td style={{ fontSize: 12.5 }}>{k.email || '—'}</td>
                  <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{k.celularLegible || '—'}</td>
                  <td style={{ fontSize: 12.5 }}>{k.ramos.map(r => ramos[r] || r).join(', ') || '—'}</td>
                  <td style={{ fontSize: 12.5 }}>{k.polizas.length ? <span style={{ color: 'var(--green)', fontWeight: 600 }}>{k.polizas.map(r => ramos[r] || r).join(', ')}</span> : '—'}</td>
                  <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{fecha(k.ultima)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {k.email && <button type="button" className={`badge ${k.bajaMail ? 'badge-red' : 'badge-green'}`} style={{ border: 'none', cursor: 'pointer', marginRight: 4, fontFamily: 'inherit' }}
                      title={k.bajaMail ? 'Está de baja: tocá para volver a mandarle' : 'Tocá para darlo de baja del mail'}
                      onClick={() => { if (window.confirm(k.bajaMail ? `¿Volver a mandarle mails a ${k.email}?` : `¿Dar de baja a ${k.email} de los mails?`)) baja({ email: k.email }, 'mail', k.bajaMail ? false : true); }}>
                      Mail {k.bajaMail ? '✕' : '✓'}</button>}
                    {k.celular && <button type="button" className={`badge ${k.bajaWa ? 'badge-red' : 'badge-green'}`} style={{ border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                      title={k.bajaWa ? 'Está de baja: tocá para volver a mandarle' : 'Tocá para darlo de baja de WhatsApp'}
                      onClick={() => { if (window.confirm(k.bajaWa ? `¿Volver a mandarle WhatsApp a ${k.celularLegible}?` : `¿Dar de baja a ${k.celularLegible} de WhatsApp?`)) baja({ celular: k.celular }, 'whatsapp', k.bajaWa ? false : true); }}>
                      WhatsApp {k.bajaWa ? '✕' : '✓'}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {lista.length > limite && (
          <div style={{ padding: 14, textAlign: 'center' }}><button className="btn btn-secondary btn-sm" onClick={() => setLimite(l => l + 200)}>Ver más ({lista.length - limite} restantes)</button></div>
        )}
      </div>
    </div>
  );
}
